import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { FolderKanban, FolderPlus, Layers, Plus, Search } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProjectOptionRow } from "@/components/ProjectOptionRow";
import { createProject, swrFetcher, type ProjectInfo } from "@/lib/api";
import { filterProjects, NO_PROJECT_LABEL, projectName } from "@/lib/projectMove";

const PROJECTS_KEY = "/api/projects?include_archived=false";
const SEARCH_THRESHOLD = 6;

interface Props {
  open: boolean;
  count: number;
  /** "generation" or "element"; pluralized with "s". */
  noun: string;
  /** Where every item already is; undefined when they differ. */
  currentProjectId: string | null | undefined;
  /** What "No project" means for this kind of item. */
  noProjectHint: string;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (projectId: string | null, targetName: string) => void;
}

/** Project picker for moving selected items; a bottom sheet on phones, a modal from sm up. */
export function MoveToProjectDialog({ open, count, noun, currentProjectId, noProjectHint, busy, error, onClose, onConfirm }: Props) {
  const { data: projects, mutate } = useSWR<ProjectInfo[]>(open ? PROJECTS_KEY : null, swrFetcher);
  const [choice, setChoice] = useState<string | null | undefined>(undefined);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setChoice(undefined);
    setQuery("");
    setCreating(false);
    setNewName("");
    setCreateError(null);
  }, [open]);

  const active = useMemo(() => projects ?? [], [projects]);
  const visible = useMemo(() => filterProjects(active, query), [active, query]);
  const title = `Move ${count} ${noun}${count === 1 ? "" : "s"}`;

  async function addProject() {
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      const project = await createProject(name);
      await mutate((current) => [...(current ?? []), project], { revalidate: true });
      setChoice(project.id);
      setCreating(false);
      setNewName("");
      setQuery("");
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Project could not be created");
    } finally {
      setCreateBusy(false);
    }
  }

  const row = (id: string | null, label: string, icon: React.ReactNode) => {
    const current = currentProjectId === id;
    return (
      <ProjectOptionRow
        key={id ?? "none"}
        icon={icon}
        label={label}
        selected={choice === id}
        disabled={busy || current}
        hint={current ? "Current" : undefined}
        onClick={() => setChoice(id)}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <div data-testid="move-project-dialog" className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 sm:p-5">
        {active.length > SEARCH_THRESHOLD && (
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-studio-faint" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search projects"
              aria-label="Search projects"
              className="pl-9"
            />
          </div>
        )}

        <div role="group" aria-label="Destination project" className="rounded-xl border border-studio-border p-1.5">
          {!query.trim() && (
            <>
              {row(null, NO_PROJECT_LABEL, <Layers className="h-4 w-4 text-studio-muted" />)}
              <p className="px-2.5 pb-1.5 text-[11px] text-studio-faint">{noProjectHint}</p>
              {active.length > 0 && <div className="my-1 border-t border-studio-border" />}
            </>
          )}
          {!projects && <p className="px-2.5 py-3 text-xs text-studio-faint">Loading projects…</p>}
          {projects && visible.length === 0 && query.trim() && (
            <p className="px-2.5 py-3 text-xs text-studio-faint">No project matches “{query.trim()}”.</p>
          )}
          {visible.map((project) => row(project.id, project.name, <FolderKanban className="h-4 w-4 text-studio-accent" />))}
        </div>

        {creating ? (
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="New project name"
              aria-label="New project name"
              maxLength={160}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void addProject();
                }
              }}
            />
            <Button type="button" variant="secondary" size="icon" loading={createBusy} disabled={!newName.trim()} onClick={() => void addProject()} aria-label="Create project">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            disabled={busy}
            className="flex min-h-11 items-center gap-2 rounded-lg px-2.5 text-sm text-studio-muted transition-colors hover:bg-studio-card hover:text-studio-ink focus-visible:outline-none focus-visible:shadow-glow-sm"
          >
            <FolderPlus className="h-4 w-4" />
            New project
          </button>
        )}

        {(error || createError) && (
          <p role="alert" className="rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{error || createError}</p>
        )}
      </div>
      <div className="grid shrink-0 grid-cols-1 gap-2 border-t border-studio-border px-4 py-4 sm:flex sm:justify-end sm:px-5">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          loading={busy}
          disabled={choice === undefined}
          onClick={() => choice !== undefined && onConfirm(choice, projectName(active, choice))}
        >
          {choice === undefined ? "Choose a project" : choice === null ? "Remove from project" : `Move to ${projectName(active, choice)}`}
        </Button>
      </div>
    </Dialog>
  );
}
